'use client';

import * as signalR from '@microsoft/signalr';
import {
  HttpTransportType,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import {
  buildUpsertEntityHubUrl,
  getSignalRHubBaseUrl,
} from '@/lib/signalrHubUrl';

async function fetchSignalRToken(): Promise<string> {
  const res = await fetch('/api/signalr-token', { credentials: 'include' });
  if (!res.ok) throw new Error(`Failed to fetch SignalR token: ${res.status}`);
  const data = await res.json();
  return data.token;
}

type HubHandler = (...args: unknown[]) => void;

const RELEASE_DELAY_MS = 30_000;
const RECONNECT_DELAYS_MS = [0, 5_000, 15_000, 30_000];

class UpsertEntityHubConnectionManager {
  private userGuid: string | null = null;
  private connection: signalR.HubConnection | null = null;
  private startPromise: Promise<void> | null = null;
  private refCount = 0;
  private releaseTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly handlers = new Map<string, Set<HubHandler>>();
  private readonly persistentHandlers = new Map<string, Set<HubHandler>>();
  private readonly attachedHandlers = new Set<HubHandler>();

  subscribePersistent(
    userGuid: string,
    eventName: string,
    handler: HubHandler,
  ): void {
    if (this.userGuid && this.userGuid !== userGuid) {
      void this.stopNow();
    }

    this.userGuid = userGuid;
    this.cancelScheduledRelease();

    if (!this.persistentHandlers.has(eventName)) {
      this.persistentHandlers.set(eventName, new Set());
    }

    this.persistentHandlers.get(eventName)!.add(handler);
    void this.attachHandler(eventName, handler);
  }

  subscribe(
    userGuid: string,
    eventName: string,
    handler: HubHandler,
  ): () => void {
    if (this.userGuid && this.userGuid !== userGuid) {
      void this.stopNow();
    }

    this.userGuid = userGuid;
    this.refCount += 1;
    this.cancelScheduledRelease();

    if (!this.handlers.has(eventName)) {
      this.handlers.set(eventName, new Set());
    }

    this.handlers.get(eventName)!.add(handler);
    void this.attachHandler(eventName, handler);

    return () => {
      this.handlers.get(eventName)?.delete(handler);
      if (this.connection) {
        this.connection.off(eventName, handler);
      }
      this.attachedHandlers.delete(handler);

      this.refCount = Math.max(0, this.refCount - 1);
      if (this.refCount === 0) {
        this.scheduleRelease();
      }
    };
  }

  private async attachHandler(eventName: string, handler: HubHandler) {
    try {
      await this.ensureStarted();
      if (!this.connection || this.attachedHandlers.has(handler)) return;

      this.connection.on(eventName, handler);
      this.attachedHandlers.add(handler);
    } catch (error) {
      console.error('[UpsertEntityHub] Connection failed', error);
    }
  }

  private cancelScheduledRelease() {
    if (this.releaseTimer) {
      clearTimeout(this.releaseTimer);
      this.releaseTimer = null;
    }
  }

  private scheduleRelease() {
    if (this.hasPersistentHandlers()) return;

    this.cancelScheduledRelease();
    this.releaseTimer = setTimeout(() => {
      if (this.refCount === 0 && !this.hasPersistentHandlers()) {
        void this.stopNow();
      }
    }, RELEASE_DELAY_MS);
  }

  private hasPersistentHandlers() {
    for (const handlers of this.persistentHandlers.values()) {
      if (handlers.size > 0) return true;
    }
    return false;
  }

  private buildConnection(userGuid: string) {
    const hubUrl = buildUpsertEntityHubUrl(userGuid);
    const viaProxy = getSignalRHubBaseUrl().includes('/api/proxy/signalr');

    return new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: fetchSignalRToken,
        // Next.js route handlers cannot upgrade WebSockets/SSE streams.
        ...(viaProxy ? { transport: HttpTransportType.LongPolling } : {}),
      })
      .withAutomaticReconnect(RECONNECT_DELAYS_MS)
      .configureLogging(LogLevel.Error)
      .build();
  }

  private async ensureStarted(): Promise<void> {
    if (!this.userGuid) return;

    if (this.connection?.state === HubConnectionState.Connected) return;

    if (this.startPromise) {
      await this.startPromise;
      return;
    }

    this.connection = this.buildConnection(this.userGuid);

    this.startPromise = this.connection
      .start()
      .then(() => undefined)
      .finally(() => {
        this.startPromise = null;
      });

    await this.startPromise;
  }

  private async stopNow() {
    this.cancelScheduledRelease();
    this.startPromise = null;
    this.userGuid = null;
    this.persistentHandlers.clear();
    this.handlers.clear();
    this.attachedHandlers.clear();

    if (!this.connection) return;

    const connection = this.connection;
    this.connection = null;

    try {
      await connection.stop();
    } catch {
      // ignore shutdown errors
    }
  }
}

export const upsertEntityHubConnection = new UpsertEntityHubConnectionManager();
